import { useState } from "react";
import { animate, motion, useMotionValue, useTransform } from "framer-motion";
import { useEffect } from "react";
import { Slider } from "@/components/ui/slider";
import { Reveal, Eyebrow } from "./Reveal";

const SAVING = 0.5;
const fmt = (n) => Math.round(n).toLocaleString("de-DE");

const Counter = ({ value, testId }) => {
  const mv = useMotionValue(value);
  const text = useTransform(mv, (v) => fmt(v));
  useEffect(() => {
    const c = animate(mv, value, { duration: 0.6, ease: "easeOut" });
    return () => c.stop();
  }, [value, mv]);
  return <motion.span data-testid={testId}>{text}</motion.span>;
};

const Field = ({ label, value, unit, min, max, step, onChange, testId }) => (
  <div>
    <div className="flex items-baseline justify-between">
      <span className="text-sm text-[var(--mist)]">{label}</span>
      <span className="font-mono text-sm text-[var(--foam)]">{value.toLocaleString("de-DE")} {unit}</span>
    </div>
    <Slider data-testid={testId} className="mt-4" value={[value]} min={min} max={max} step={step} onValueChange={(v) => onChange(v[0])} />
  </div>
);

export const Calculator = () => {
  const [watt, setWatt] = useState(1100);
  const [hours, setHours] = useState(10);
  const [days, setDays] = useState(150);
  const [price, setPrice] = useState(35);
  const kwh = (watt / 1000) * hours * days;
  const cost = (kwh * price) / 100;

  return (
    <section id="rechner" data-testid="calculator-section" className="relative overflow-hidden border-y border-white/10 bg-[var(--deep)]">
      <div className="mx-auto grid max-w-[1400px] gap-14 px-5 py-24 sm:px-8 sm:py-32 lg:grid-cols-2">
        <div>
          <Reveal><Eyebrow>Beispielrechnung</Eyebrow></Reveal>
          <Reveal delay={0.08}><h2 className="mt-6 font-display text-4xl sm:text-5xl font-semibold leading-[1] tracking-[-0.03em]">Was kostet Ihre alte Pumpe pro Saison?</h2></Reveal>
          <Reveal delay={0.14}><p className="mt-6 max-w-md text-[var(--mist)]">Stellen Sie Ihre Werte ein. Das Ergebnis ist eine grobe Orientierung – Ihr tatsächliches Potenzial klären wir in der Beratung.</p></Reveal>
          <Reveal delay={0.2} className="mt-12 space-y-9">
            <Field testId="calc-watt" label="Pumpenleistung" unit="W" value={watt} min={300} max={2500} step={50} onChange={setWatt} />
            <Field testId="calc-hours" label="Laufzeit pro Tag" unit="h" value={hours} min={2} max={24} step={1} onChange={setHours} />
            <Field testId="calc-days" label="Badesaison" unit="Tage" value={days} min={60} max={365} step={5} onChange={setDays} />
            <Field testId="calc-price" label="Strompreis" unit="ct/kWh" value={price} min={20} max={60} step={1} onChange={setPrice} />
          </Reveal>
        </div>
        <Reveal delay={0.1} className="lg:pl-10">
          <div className="sticky top-28 rounded-[28px] border border-white/10 bg-[#04171B] p-8 sm:p-10">
            <p className="font-mono text-xs uppercase tracking-[0.25em] text-[var(--signal)]">Heute · Stromkosten Pumpe</p>
            <p className="mt-3 font-display text-6xl sm:text-7xl font-semibold tracking-[-0.04em]"><Counter value={cost} testId="calc-cost-now" /> €</p>
            <p className="mt-2 text-sm text-[var(--mist)]"><Counter value={kwh} testId="calc-kwh" /> kWh pro Saison</p>
            <div className="my-8 h-px bg-white/10" />
            <p className="font-mono text-xs uppercase tracking-[0.25em] text-[var(--lagoon)]">Beispiel · bei {SAVING * 100} % weniger Pumpenstrom</p>
            <p className="mt-3 font-display text-6xl sm:text-7xl font-semibold tracking-[-0.04em] text-[var(--lagoon)]"><Counter value={cost * SAVING} testId="calc-saving" /> €</p>
            <p className="mt-2 text-sm text-[var(--mist)]">mögliche Ersparnis pro Saison</p>
            <p className="mt-8 text-xs leading-relaxed text-[var(--mist)]">Annahme zur Veranschaulichung, keine Zusage. Die tatsächliche Einsparung hängt von Anlage, Pumpe und Nutzung ab.</p>
          </div>
        </Reveal>
      </div>
    </section>
  );
};
